import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, AlertCircle } from 'lucide-react';
import { StatCard } from '../StatCard';
import { Badge } from '../SubscriptionAppBadge';
import { Button } from '../SubscriptionAppButton';
import { Pagination } from '../SubscriptionAppPagination';
import { mockBulkJobs, ACTION_LABELS, JOB_STATUS_LABELS, BulkJob, BulkJobStatus } from '../../data/mockBulkUpdateJobs';
const statusVariant: Record<BulkJobStatus, 'success' | 'warning' | 'error' | 'info'> = {
  running: 'info',
  completed: 'success',
  completedWithFailures: 'warning',
  failed: 'error'
};
function JobStatusBadge({
  status
}: {
  status: BulkJobStatus;
}) {
  return <Badge variant={statusVariant[status]} data-id="element-804" data-mp="1fdkkkadfb1o0">{JOB_STATUS_LABELS[status]}</Badge>;
}
/* ------------------------------------------------------------------ */
/* Job list                                                            */
/* ------------------------------------------------------------------ */
function JobList({
  jobs,
  onOpenJob
}: {
  jobs: BulkJob[];
  onOpenJob: (id: string) => void;
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const totals = useMemo(() => ({
    total: jobs.length,
    running: jobs.filter(j => j.status === 'running').length,
    withFailures: jobs.filter(j => j.failed > 0).length
  }), [jobs]);
  const totalPages = Math.max(1, Math.ceil(jobs.length / rowsPerPage));
  const pageJobs = jobs.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const handleRowsPerPageChange = (next: number) => {
    setRowsPerPage(next);
    setCurrentPage(1);
  };
  return <div data-id="element-805" data-mp="2248kac9ljy8a">
      {/* Summary tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6" data-id="element-806" data-mp="225ba0gv6numm">
        <StatCard label="Total Jobs" value={totals.total} data-id="element-807" data-mp="39gihhl9412ig" />
        <StatCard label="Running" value={totals.running} secondaryValue={totals.running === 0 ? 'Nothing in progress' : 'Updating in the background'} data-id="element-808" data-mp="3dj5dlyctd9lg" />
        <StatCard label="With Failures" value={totals.withFailures} badge={totals.withFailures > 0 ? {
        text: 'Needs review',
        variant: 'warning'
      } : undefined} secondaryValue={totals.withFailures > 0 ? 'Some members were not updated' : undefined} data-id="element-809" data-mp="1ecu47n27x1j7" />
      </div>

      {/* History table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-810" data-mp="1igs3pyvggecs">
        <div className="px-5 py-4 border-b border-[#E2E8F0]" data-id="element-811" data-mp="urfwh4jougd4">
          <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-812" data-mp="qosz6o49038j">
            Bulk update history
          </h2>
          <p className="text-[13px] text-[#64748B] mt-1" data-id="element-813" data-mp="14gr5kr6krl10">
            Every bulk change you've run, and how each one finished.
          </p>
        </div>

        {jobs.length === 0 ? <div className="px-5 py-12 text-center" data-id="element-814" data-mp="bzwxd8ssbmgd">
            <p className="text-[14px] font-medium text-[#0F172A]" data-id="element-815" data-mp="byls14joy6n5">
              No bulk updates yet
            </p>
            <p className="text-[13px] text-[#64748B] mt-1" data-id="element-816" data-mp="70jytqsjwqxy">
              Select subscriptions on the Subscriptions tab and choose
              Reschedule or Update Status to run your first one.
            </p>
          </div> : <>
            <div className="overflow-x-auto" data-id="element-817" data-mp="29nh4dht88nr4">
              <table className="w-full" data-id="element-818" data-mp="29os8jiql3xir">
                <thead data-id="element-819" data-mp="29lo1mdlqinoe">
                  <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-820" data-mp="2em2a1wr8zzmy">
                    {['Job', 'Action', 'Status', 'Affected', 'Completed', 'Failed', 'Run by', 'Created'].map((heading, i) => <th key={heading} className={`px-5 py-3 text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px] whitespace-nowrap ${[3, 4, 5].includes(i) ? 'text-right' : 'text-left'}`} data-id="element-821" data-mp="36drhz12wn5cj" data-mpi={i}>
                          {heading}
                        </th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-822" data-mp="1gyaawx8mqocq">
                  {pageJobs.map(job => <tr key={job.id} className="hover:bg-[#F8FAFC] transition-colors duration-200" data-id="element-823" data-mp="1lxwnr5kjq2l0">
                      <td className="px-5 py-4" data-id="element-824" data-mp="217jmcyy1qxa6">
                        <button onClick={() => onOpenJob(job.id)} className="font-mono text-[13px] text-[#0EA5E9] hover:text-[#0284C7] hover:underline focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 rounded" data-id="element-825" data-mp="2194ekhww244v">
                          {job.id}
                        </button>
                      </td>
                      <td className="px-5 py-4 text-[14px] text-[#0F172A] whitespace-nowrap" data-id="element-826" data-mp="1nebr8xarbjnq">
                        {ACTION_LABELS[job.action]}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap" data-id="element-827" data-mp="2g5bwfbktqtgz">
                        <JobStatusBadge status={job.status} data-id="element-828" data-mp="2b4xyimcxszwo" />
                      </td>
                      <td className="px-5 py-4 text-[14px] text-[#0F172A] text-right" data-id="element-829" data-mp="2675wgzikvxer">
                        {job.affected}
                      </td>
                      <td className="px-5 py-4 text-[14px] text-[#0F172A] text-right" data-id="element-830" data-mp="zrvknz282og7">
                        {job.completed}
                      </td>
                      <td className={`px-5 py-4 text-[14px] text-right font-medium ${job.failed > 0 ? 'text-[#DC2626]' : 'text-[#94A3B8]'}`} data-id="element-831" data-mp="kobyzj0xux3d">
                        {job.failed}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap" data-id="element-832" data-mp="ulgptw5qbgqi">
                        <div className="text-[14px] text-[#0F172A]" data-id="element-833" data-mp="zcgmd0axrwsh">
                          {job.actorName}
                        </div>
                        <div className="text-[12px] text-[#94A3B8]" data-id="element-834" data-mp="13d8t9mwc5pll">
                          {job.actorEmail}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-[14px] text-[#475569] whitespace-nowrap" data-id="element-835" data-mp="1dchmgdjdn06w">
                        {job.createdAt}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-4 border-t border-[#E2E8F0]" data-id="element-836" data-mp="3f55rmsfkzxpb">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} itemsPerPage={rowsPerPage} totalItems={jobs.length} onItemsPerPageChange={handleRowsPerPageChange} itemLabel="jobs" data-id="element-837" data-mp="3k1w7benh4ra7" />
            </div>
          </>}
      </div>
    </div>;
}
/* ------------------------------------------------------------------ */
/* Job detail                                                          */
/* ------------------------------------------------------------------ */
function JobDetail({
  job,
  onBack
}: {
  job: BulkJob;
  onBack: () => void;
}) {
  const navigate = useNavigate();
  const failedItems = job.items.filter(item => item.status === 'failed');
  const changeLabel = job.action === 'reschedule' ? 'New charge date' : 'New status';
  const changeValue = job.action === 'reschedule' ? job.newBillingDate : job.newStatus;
  const summaryFields = [{
    label: 'Run by',
    value: `${job.actorName} (${job.actorEmail})`
  }, {
    label: 'Created',
    value: job.createdAt
  }, {
    label: 'Finished',
    value: job.finishedAt ?? 'Still running'
  }, {
    label: changeLabel,
    value: changeValue ?? '\u2014'
  }, {
    label: 'Members notified',
    value: job.notify ? 'Yes' : 'No'
  }];
  return <div data-id="element-838" data-mp="116y7cndcqfqz">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-[14px] text-[#0EA5E9] hover:text-[#0284C7] mb-5 focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 rounded" data-id="element-839" data-mp="115n2ek8671ya">
        <ArrowLeft size={16} data-id="element-840" data-mp="di58oj5chtso" />
        Back to jobs
      </button>

      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-5" data-id="element-841" data-mp="dgc0gzf331ik">
        <h1 className="font-mono text-[24px] font-semibold text-[#0F172A]" data-id="element-842" data-mp="i9na18ba5we6">
          {job.id}
        </h1>
        <span className="text-[14px] text-[#64748B]" data-id="element-843" data-mp="4j1rjkpqnogt">
          {ACTION_LABELS[job.action]}
        </span>
        <JobStatusBadge status={job.status} data-id="element-844" data-mp="8jbvkrtz04t1" />
      </div>

      {/* Summary */}
      <div className="bg-white rounded-lg shadow-sm p-5 mb-6" data-id="element-845" data-mp="3qpzws6lwg7tf">
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4" data-id="element-846" data-mp="3vni7a1fkev3j">
          {summaryFields.map(field => <div key={field.label} data-id="element-847" data-mp="f0nf1vht4yhj">
              <dt className="text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px] mb-1" data-id="element-848" data-mp="ezm0vi5hvvvl">
                {field.label}
              </dt>
              <dd className="text-[14px] text-[#0F172A]" data-id="element-849" data-mp="5z7ba6lhzuma">{field.value}</dd>
            </div>)}
        </dl>

        {job.notify && job.notifyMessage && <div className="mt-5 pt-5 border-t border-[#E2E8F0]" data-id="element-850" data-mp="10clgkp455so">
            <dt className="text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px] mb-2" data-id="element-851" data-mp="3s76xfdlcvuoj">
              Message sent to members
            </dt>
            <dd className="text-[14px] text-[#475569] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3" data-id="element-852" data-mp="3nedsoppw0mcg">
              {job.notifyMessage}
            </dd>
          </div>}
      </div>

      {/* Counts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6" data-id="element-853" data-mp="39gftirk50whj">
        <StatCard label="Affected" value={job.affected} data-id="element-854" data-mp="39iihz75n6hk5" />
        <StatCard label="Completed" value={job.completed} data-id="element-855" data-mp="34h2yvkn0yl4e" />
        <StatCard label="Failed" value={job.failed} badge={job.failed > 0 ? {
        text: 'Needs review',
        variant: 'error'
      } : undefined} data-id="element-856" data-mp="1jzaiz8mm09k4" />
      </div>

      {/* Failure callout */}
      {failedItems.length > 0 && <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-4 flex flex-wrap items-center justify-between gap-4 mb-6" data-id="element-857" data-mp="1twygu7fihx6i">
          <div className="flex gap-3" data-id="element-858" data-mp="1xyjmb7ppv96o">
            <AlertCircle className="text-[#D97706] shrink-0" size={20} data-id="element-859" data-mp="118stre0zjw3i" />
            <div data-id="element-860" data-mp="11aneosc9bo7l">
              <h3 className="text-[14px] font-semibold text-[#92400E]" data-id="element-861" data-mp="116zl5t6tb1sv">
                {failedItems.length}{' '}
                {failedItems.length === 1 ? 'subscription was' : 'subscriptions were'}{' '}
                not updated
              </h3>
              <p className="text-[13px] text-[#B45309] mt-1" data-id="element-862" data-mp="1f10n0vt294xg">
                Fix the reason given below, then retry just the ones that
                failed. The rest are already updated and won't be touched.
              </p>
            </div>
          </div>
          <Button variant="outline" size="md" iconLeft={<RefreshCw size={16} data-id="element-864" data-mp="3jma4q9wy3369" />} data-id="element-863" data-mp="3jnbp54zup20n">
            Retry {failedItems.length} failed
          </Button>
        </div>}

      {/* Per-subscription results */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-865" data-mp="3snzt16vj5lzt">
        <div className="px-5 py-4 border-b border-[#E2E8F0]" data-id="element-866" data-mp="3slnbgutopdna">
          <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-867" data-mp="1gdrbjsdftll">
            Subscriptions ({job.items.length})
          </h2>
        </div>

        <div className="overflow-x-auto" data-id="element-868" data-mp="30uqylu7mj900">
          <table className="w-full" data-id="element-869" data-mp="34xdz0jid228t">
            <thead data-id="element-870" data-mp="34yf7cg7zsamu">
              <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-871" data-mp="34vt4gtw1qjay">
                {['Subscription', 'Member', 'Result', 'Reason', 'Processed'].map(heading => <th key={heading} className="px-5 py-3 text-left text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px] whitespace-nowrap" data-id="element-872" data-mp="3ep0dqptwqjgb">
                      {heading}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]" data-id="element-873" data-mp="2m5bc4qf4lpq4">
              {job.items.map(item => <tr key={item.subscriptionId} className="hover:bg-[#F8FAFC] transition-colors duration-200 align-top" data-id="element-874" data-mp="2endepka7y539">
                  <td className="px-5 py-4 whitespace-nowrap" data-id="element-875" data-mp="24pz0zsfojuen">
                    <button onClick={() => navigate(`/subscriptions/${item.subscriptionId}`)} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] hover:underline focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 rounded" data-id="element-876" data-mp="24oe78516wfhu">
                      {item.subscriptionId}
                    </button>
                  </td>
                  <td className="px-5 py-4 text-[14px] text-[#0F172A] whitespace-nowrap" data-id="element-877" data-mp="1lz7uqzqzok4h">
                    {item.customerName}
                  </td>
                  <td className="px-5 py-4 whitespace-nowrap" data-id="element-878" data-mp="1h1z92p6mtxyh">
                    {item.status === 'failed' ? <Badge variant="error" data-id="element-879" data-mp="1cy9qcgcn2ald">Failed</Badge> : item.status === 'pending' ? <Badge variant="info" data-id="element-880" data-mp="17zgaih9vq7i1">Pending</Badge> : <div data-id="element-881" data-mp="180hytnfwoqhp">
                        <Badge variant="success" data-id="element-882" data-mp="yc3yn2ebp5fa">Updated</Badge>
                        {item.result && <div className="text-[13px] text-[#475569] mt-1" data-id="element-883" data-mp="ye6z6gd3qgy9">
                            {item.result}
                          </div>}
                      </div>}
                  </td>
                  <td className="px-5 py-4 text-[13px] max-w-[420px]" data-id="element-884" data-mp="ofr98gpt9dqc">
                    {item.error ? <span className="text-[#DC2626]" data-id="element-885" data-mp="tdj7dsh1tw2z">{item.error}</span> : <span className="text-[#94A3B8]" data-id="element-886" data-mp="1h225xgaaquhv">—</span>}
                  </td>
                  <td className="px-5 py-4 text-[13px] text-[#475569] whitespace-nowrap" data-id="element-887" data-mp="1m1oswhfxfp3v">
                    {item.processedAt ?? '—'}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
/* ------------------------------------------------------------------ */
/* Tab wrapper                                                         */
/* ------------------------------------------------------------------ */
export interface BulkUpdateJobsTabProps {
  jobs?: BulkJob[];
}
export function BulkUpdateJobsTab({
  jobs = mockBulkJobs
}: BulkUpdateJobsTabProps) {
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const openJob = jobs.find(job => job.id === openJobId);
  if (openJob) {
    return <JobDetail job={openJob} onBack={() => setOpenJobId(null)} data-id="element-888" data-mp="24pw78qvhpfe" />;
  }
  return <JobList jobs={jobs} onOpenJob={setOpenJobId} data-id="element-889" data-mp="3ual812y5j8z7" />;
}